import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { ChatRuntimeProvider } from "@src/features/assistantChat/ChatRuntimeProvider";
import { AiChatType } from "@src/types.generated";
import NewModuleDialog from "@src/features/contentLibrary/NewModuleDialog";
import ModuleOutlineCanvasDialog from "@src/features/contentLibrary/ModuleOutlineCanvasDialog";
import NewModuleFromArticulateUploadDialog from "@src/features/contentLibrary/NewModuleFromArticulateUploadDialog";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { transformToSelectedSession } from "@src/features/aiAssist/utils/sessionTransform";
import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onModuleCreated?: (libraryItemId: string) => void;
  initialInput?: string;
};

const ModuleCreationFlowDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onModuleCreated,
  initialInput,
}) => {
  const router = useRouter();
  const [showCanvas, setShowCanvas] = useState(false);
  const [isArticulateDialogOpen, setIsArticulateDialogOpen] = useState(false);
  const { selectSession } = useChatSidebar(AiChatType.ModuleOutline);

  useEffect(() => {
    if (!open) setShowCanvas(false);
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setShowCanvas(false);
    onOpenChange(nextOpen);
  };

  const handleSelectHistoricalChat = (chat: AiChatFragment) => {
    // Transform and load the historical chat
    const selectedSession = transformToSelectedSession(chat);
    selectSession(selectedSession);

    // Skip to canvas since we have an existing conversation
    setShowCanvas(true);
  };

  return (
    <ChatRuntimeProvider chatType={AiChatType.ModuleOutline}>
      <NewModuleDialog
        open={open && !showCanvas}
        onOpenChange={handleOpenChange}
        onStartNewChat={() => setShowCanvas(true)}
        initialInput={initialInput}
        onImportArticulate={() => {
          // Close flow and open Articulate upload dialog
          handleOpenChange(false);
          setIsArticulateDialogOpen(true);
        }}
        enableChatHistory={true}
        onSelectHistoricalChat={handleSelectHistoricalChat}
      />

      <ModuleOutlineCanvasDialog
        dialogIsOpen={open && showCanvas}
        setDialogIsopen={(isOpen) => {
          if (!isOpen) handleOpenChange(false);
        }}
        onModuleCreated={(libraryItemId) => {
          if (onModuleCreated) {
            onModuleCreated(libraryItemId);
            return;
          }
          if (libraryItemId) {
            router.push({
              pathname: "/library/library-item/[id]",
              query: { id: libraryItemId },
            });
          }
        }}
      />

      <NewModuleFromArticulateUploadDialog
        open={isArticulateDialogOpen}
        onOpenChange={setIsArticulateDialogOpen}
      />
    </ChatRuntimeProvider>
  );
};

export default ModuleCreationFlowDialog;
